/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useChannelChoices,
  useNoticeTemplates,
} from "@features/cx/outstanding/hooks";
import { formatFileSize, validateFile } from "@features/cx/shared/utils/file";
import { DynamicForm } from "@shared/components/DynamicForm";
import { PageHeader } from "@shared/components/PageHeader";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  ChevronRight,
  ChevronLeft,
  Upload,
  FileText,
  File,
  Image,
  X,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface FileData {
  id: string;
  file?: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
  needsReupload?: boolean;
}

interface NoticeDetailsProps {
  data?: {
    communicationChannel?: string;
    noticeTemplate?: string | number;
    templateName?: string;
    attachments?: Array<{
      id: string;
      name: string;
      size: number;
      type: string;
      needsReupload?: boolean;
    }>;
  };
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
}

export const NoticeDetails: React.FC<NoticeDetailsProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  stepHelpers,
  currentStepIndex = 1,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dataLoadedRef = useRef(false);
  const savedStepDataRef = useRef<any>(null);

  const [isStepDataLoaded, setIsStepDataLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);
  const [selectedChannelType, setSelectedChannelType] = useState<string>("");

  const { data: templateList } = useNoticeTemplates(selectedChannelType);
  const { data: channelList } = useChannelChoices("quick_messaging");

  const channelChoices = useMemo(
    () =>
      channelList?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [channelList],
  );

  const templateChoices = useMemo(() => {
    return (
      templateList?.map((temp: any) => ({
        label: temp.templateName,
        value: temp.id,
      })) || []
    );
  }, [templateList]);

  // Helper functions using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  const saveStepData = useCallback(
    (stepDataToSave: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...stepDataToSave,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  const getFileIcon = (type: string) => {
    if (type === "application/pdf") {
      return <FileText className="h-8 w-8 text-red-500" />;
    } else if (type.startsWith("image/")) {
      return <Image className="h-8 w-8 text-blue-500" />;
    }
    return <File className="h-8 w-8 text-gray-500" />;
  };

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const newFiles: FileData[] = [];
      const errors: string[] = [];

      Array.from(files).forEach((file) => {
        const validationError = validateFile(file);
        if (validationError) {
          errors.push(validationError);
          return;
        }

        const isDuplicate = selectedFiles.some(
          (f) => f.name === file.name && f.size === file.size,
        );
        if (isDuplicate) {
          errors.push(`File "${file.name}" is already selected`);
          return;
        }

        const fileData: FileData = {
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          needsReupload: false,
        };

        newFiles.push(fileData);
      });

      if (errors.length > 0) {
        toast({
          title: "File Upload Errors",
          description: errors.join(", "),
          variant: "destructive",
        });
      }

      if (newFiles.length > 0) {
        const updatedFiles = [...selectedFiles, ...newFiles];
        setSelectedFiles(updatedFiles);

        const currentData = getStepData() || {};
        const stepData = {
          ...currentData,
          attachments: updatedFiles.map((f) => ({
            id: f.id,
            name: f.name,
            size: f.size,
            type: f.type,
          })),
        };
        saveStepData(stepData);

        if (onDataChange) {
          const submissionData = {
            ...stepData,
            file: updatedFiles
              .filter((f) => !f.needsReupload && f.file)
              .map((f) => f.file!),
            fileMetadata: updatedFiles.map((f) => ({
              id: f.id,
              name: f.name,
              size: f.size,
              type: f.type,
              needsReupload: f.needsReupload || false,
            })),
          };
          onDataChange(submissionData);
          (window as any).bulkNoticeFileData = {
            fileObjects: submissionData.file,
          };
        }
      }

      if (event.target) {
        event.target.value = "";
      }
    },
    [selectedFiles, getStepData, saveStepData, onDataChange],
  );

  const removeFile = useCallback(
    (fileId: string) => {
      const updatedFiles = selectedFiles.filter((f) => f.id !== fileId);
      setSelectedFiles(updatedFiles);

      const currentData = getStepData() || {};
      const stepData = {
        ...currentData,
        attachments: updatedFiles.map((f) => ({
          id: f.id,
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      };
      saveStepData(stepData);

      if (onDataChange) {
        const submissionData = {
          ...stepData,
          file: updatedFiles
            .filter((f) => !f.needsReupload && f.file)
            .map((f) => f.file!),
          fileMetadata: updatedFiles.map((f) => ({
            id: f.id,
            name: f.name,
            size: f.size,
            type: f.type,
            needsReupload: f.needsReupload || false,
          })),
        };
        onDataChange(submissionData);
        (window as any).bulkNoticeFileData = {
          fileObjects: submissionData.file,
        };
      }
    },
    [selectedFiles, getStepData, saveStepData, onDataChange],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const files = e.dataTransfer.files;
      if (files) {
        const mockEvent = {
          target: { files, value: "" },
        } as React.ChangeEvent<HTMLInputElement>;
        handleFileUpload(mockEvent);
      }
    },
    [handleFileUpload],
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "communicationChannel",
        label: "Communication Channel",
        type: "select",
        required: true,
        placeholder: "Select Channel",
        options: channelChoices,
        className: "col-span-12",
      },
      {
        name: "noticeTemplate",
        label: "Notice Template",
        type: "select",
        required: true,
        placeholder: "Select Template",
        options: templateChoices,
        className: "col-span-12",
      },
      {
        name: "attachments",
        label: "Attachments",
        type: "custom",
        fullWidth: true,
        className: "col-span-12 mt-2",
        customRender: () => (
          <div className="space-y-4 pt-2">
            <div
              className="border-2 border-dashed rounded-lg p-6 flex items-center gap-6 hover:bg-blue-50/50 transition-colors cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="p-4 rounded-full shrink-0 ml-4 bg-gray-50 group-hover:bg-blue-100 transition-colors">
                <Upload className="h-8 w-8 text-gray-400 group-hover:text-blue-500" />
              </div>
              <div className="flex flex-col gap-2 flex-grow ml-6">
                <h3 className="font-medium text-gray-900">Upload Files</h3>
                <p className="text-sm text-gray-600">
                  Drag & drop files here or click to browse
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Choose Files
                  </Button>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Max size: 10MB, Formats: PDF, JPG, PNG
                  </p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileUpload}
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <File className="h-4 w-4 text-blue-500" />
                    Selected Files ({selectedFiles.length})
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedFiles.map((fileData) => (
                    <div
                      key={fileData.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg transition-all ${
                        fileData.needsReupload
                          ? "border-orange-200 bg-orange-50/50"
                          : "border-gray-100 bg-gray-50/50 hover:border-blue-200 hover:bg-blue-50/30"
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {getFileIcon(fileData.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {fileData.name}
                          </p>
                          {fileData.needsReupload && (
                            <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">
                              Restore Required
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500">
                          {formatFileSize(fileData.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(fileData.id);
                        }}
                        className="flex-shrink-0 p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-md transition-colors"
                        title="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ),
      },
    ],
    [
      selectedFiles,
      channelChoices,
      templateChoices,
      handleFileUpload,
      removeFile,
      handleDrop,
    ],
  );

  const noticeForm = FormService.createForm<any>({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      communicationChannel: "",
      noticeTemplate: "",
    },
  });

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        savedStepDataRef.current = savedStepData;

        if (savedStepData) {
          console.log("Loaded notice details step data:", savedStepData);

          const formDataToSet = {
            communicationChannel: savedStepData.communicationChannel || "",
            noticeTemplate:
              savedStepData.noticeTemplate !== undefined &&
              savedStepData.noticeTemplate !== null
                ? String(savedStepData.noticeTemplate)
                : "",
          };

          FormService.populateForm(noticeForm, formDataToSet, formFields);

          if (
            savedStepData.attachments &&
            Array.isArray(savedStepData.attachments)
          ) {
            const windowFiles =
              (window as any).bulkNoticeFileData?.fileObjects || [];
            const restoredFiles: FileData[] = savedStepData.attachments.map(
              (fileMetadata: any) => {
                const actualFile = windowFiles.find(
                  (f: File) =>
                    f.name === fileMetadata.name &&
                    f.size === fileMetadata.size,
                );
                return {
                  id: fileMetadata.id,
                  name: fileMetadata.name,
                  size: fileMetadata.size,
                  type: fileMetadata.type,
                  needsReupload: !actualFile,
                  file: actualFile,
                };
              },
            );
            setSelectedFiles(restoredFiles);
          }

          if (onDataChange) {
            const submissionData = {
              ...savedStepData,
              file: (savedStepData.attachments || [])
                .map((meta: any) => {
                  return (
                    (window as any).bulkNoticeFileData?.fileObjects || []
                  ).find(
                    (f: File) => f.name === meta.name && f.size === meta.size,
                  );
                })
                .filter(Boolean),
              fileMetadata: savedStepData.attachments || [],
            };
            onDataChange(submissionData);
          }
        }
        dataLoadedRef.current = true;
        setIsStepDataLoaded(true);
      } catch (error) {
        console.error("Failed to load step data", error);
        dataLoadedRef.current = true;
        setIsStepDataLoaded(true);
      }
    }
  }, [stepHelpers, noticeForm, getStepData, formFields, onDataChange]);

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      if (value === undefined || value === null) return;

      const currentFormData = noticeForm.getValues();
      const currentData = getStepData() || {};

      const updated: any = {
        ...currentData,
        ...currentFormData,
        attachments: currentData.attachments,
        [fieldName]: value,
      };

      if (fieldName === "communicationChannel") {
        const previousChannel = currentData.communicationChannel;
        if (previousChannel && String(previousChannel) !== String(value)) {
          updated.noticeTemplate = "";
          updated.templateName = "";
          noticeForm.setValue("noticeTemplate", "");
        }
      }

      if (fieldName === "noticeTemplate" && templateList) {
        const selectedTemp = templateList.find(
          (temp: any) => String(temp.id) === String(value),
        );
        if (selectedTemp) {
          updated.noticeTemplate = value;
          updated.templateName = selectedTemp.templateName;
        }
      }

      saveStepData(updated);

      if (onDataChange) {
        const fileObjects = selectedFiles
          .filter((f) => !f.needsReupload && f.file)
          .map((f) => f.file!);
          
        onDataChange({
          ...updated,
          file: fileObjects,
          fileMetadata: selectedFiles.map((f) => ({
            id: f.id,
            name: f.name,
            size: f.size,
            type: f.type,
            needsReupload: f.needsReupload || false,
          })),
        });
      }
    },
    [
      noticeForm,
      getStepData,
      templateList,
      saveStepData,
      onDataChange,
      selectedFiles,
    ],
  );

  const handleFormSubmit = useCallback(
    (formData: any) => {
      if (!formData.communicationChannel || !formData.noticeTemplate) {
        return;
      }

      const currentData = getStepData() || {};
      const selectedTemp = templateList?.find(
        (temp: any) => String(temp.id) === String(formData.noticeTemplate),
      );

      const finalData = {
        ...currentData,
        ...formData,
        attachments: currentData.attachments,
        noticeTemplate: formData.noticeTemplate,
        templateName:
          selectedTemp?.templateName || currentData.templateName || "",
      };

      saveStepData(finalData);

      if (onDataChange) {
        const fileObjects = selectedFiles
          .filter((f) => !f.needsReupload && f.file)
          .map((f) => f.file!);
          
        onDataChange({
          ...finalData,
          file: fileObjects,
          fileMetadata: selectedFiles.map((f) => ({
            id: f.id,
            name: f.name,
            size: f.size,
            type: f.type,
            needsReupload: f.needsReupload || false,
          })),
        });
      }

      if (onNext) {
        onNext();
      }
    },
    [
      saveStepData,
      onNext,
      getStepData,
      templateList,
      onDataChange,
      selectedFiles,
    ],
  );

  const currentTemplate = noticeForm.watch("noticeTemplate");
  const currentChannel = noticeForm.watch("communicationChannel");

  useEffect(() => {
    if (currentChannel !== undefined && currentChannel !== null) {
      const str = currentChannel.toString().toUpperCase();
      if (str === "0" || str === 0 || str === "EMAIL") {
        setSelectedChannelType("EMAIL");
      } else if (
        str === "1" ||
        str === 1 ||
        str === "SMS" ||
        str === "TEXT MESSAGE" ||
        str === "TEXT_MESSAGE"
      ) {
        setSelectedChannelType("SMS");
      } else {
        setSelectedChannelType("");
      }
    }
  }, [currentChannel]);

  // When templateList resolves and a template is selected, always sync templateName to step data
  useEffect(() => {
    if (
      currentTemplate &&
      templateList &&
      templateList.length > 0 &&
      isStepDataLoaded
    ) {
      const selectedTemp = templateList.find(
        (temp: any) => String(temp.id) === String(currentTemplate),
      );
      if (selectedTemp) {
        const currentData = getStepData() || {};
        if (currentData.templateName !== selectedTemp.templateName) {
          const updatedData = {
            ...currentData,
            communicationChannel: currentChannel,
            noticeTemplate: currentTemplate,
            templateName: selectedTemp.templateName,
          };
          saveStepData(updatedData);
          if (onDataChange) {
            onDataChange({
              ...updatedData,
              file: selectedFiles
                .filter((f) => !f.needsReupload && f.file)
                .map((f) => f.file!),
              fileMetadata: selectedFiles.map((f) => ({
                id: f.id,
                name: f.name,
                size: f.size,
                type: f.type,
                needsReupload: f.needsReupload || false,
              })),
            });
          }
        }
      }
    }
  }, [
    currentTemplate,
    currentChannel,
    templateList,
    isStepDataLoaded,
    getStepData,
    saveStepData,
    onDataChange,
    selectedFiles,
  ]);

  // Autopopulate template selection when templateList resolves (covers navigation back)
  useEffect(() => {
    if (templateList && templateList.length > 0 && isStepDataLoaded) {
      const savedStepData = savedStepDataRef.current;
      const savedTemplate =
        savedStepData?.noticeTemplate ?? data?.noticeTemplate;
      if (
        savedTemplate !== undefined &&
        savedTemplate !== null &&
        savedTemplate !== ""
      ) {
        const savedStr = String(savedTemplate);
        const currentVal = noticeForm.getValues("noticeTemplate");
        if (String(currentVal) !== savedStr) {
          noticeForm.setValue("noticeTemplate", savedStr);
        }
      }
    }
  }, [templateList, noticeForm, isStepDataLoaded, data]);

  const renderedPreview = useMemo(() => {
    if (!currentTemplate) return "";
    const selectedTemplateObj = templateList?.find(
      (temp: any) =>
        temp.id === Number(currentTemplate) || temp.id === currentTemplate,
    );
    return selectedTemplateObj?.content?.body || "";
  }, [currentTemplate, templateList]);

  const isValid = currentChannel !== "" && currentTemplate !== "";

  return (
    <div className="space-y-6">
      <PageHeader
        title={"Notice Details"}
        subtitle={"Configure parameters for notice dispatch"}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-5 border border-gray-200 rounded-lg shadow-sm">
          <DynamicForm
            form={noticeForm}
            fields={formFields}
            gridColumns={12}
            config={{
              classes: FIELD_CLASSES,
              showSubmitButton: false,
            }}
            onFieldChange={handleFieldChange}
          />
        </div>

        <div className="flex flex-col bg-gray-50 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">
              Template Preview
            </span>
            {currentTemplate && (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 rounded-full text-[10px]"
              >
                Live Preview
              </Badge>
            )}
          </div>
          <div className="flex-1 p-5 flex flex-col justify-center min-h-[300px]">
            {currentTemplate ? (
              <div
                className="bg-white border border-gray-150 rounded-lg p-5 shadow-sm font-mono text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderedPreview }}
              />
            ) : (
              <div className="text-center text-gray-400 italic text-sm">
                Select a template to view live preview rendering.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4 border-t border-gray-100">
        <Button
          onClick={onPrevious}
          className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 font-medium flex items-center gap-1.5 rounded-lg px-4 py-2"
        >
          <ChevronLeft size={16} />
          <span>Previous: Select Consumers</span>
        </Button>

        <Button
          onClick={() => handleFormSubmit(noticeForm.getValues())}
          disabled={!isValid}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center gap-1.5 rounded-lg px-4 py-2"
        >
          <span>Next: Review & Send</span>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
};

export default NoticeDetails;
